import http from '@/shared/api/http'
import type { ApiPaginationResponse, ApiResponse } from '@/shared/api/http/api/response.api'
import type {
  UserDepartmentPositionComboboxVm,
  UserDetailVm,
  UserListItemVm,
} from './types/user.model'
import type { UserCreateInput, UserGetListInput, UserUpdateInput } from './types/user.param'
import {
  apiUser,
  apiUserAvatar,
  apiUserBulkDelete,
  apiUserBulkLock,
  apiUserBulkLogout,
  apiUserBulkUnlock,
  apiUserById,
  apiUserCombobox,
  apiUserDepartmentPositionCombobox,
  apiUserLock,
  apiUserLogout,
  apiUserProfile,
  apiUserProfileAvatar,
  apiUserResetPassword,
  apiUserUnlock,
} from './user.urls'

const toQuery = (input: UserGetListInput): string => {
  const params = new URLSearchParams()
  params.set('currentPage', String(input.page))
  params.set('pageSize', String(input.pageSize))
  if (input.query) params.set('query', input.query)
  if (input.sortBy) params.set('sortBy', input.sortBy)
  if (input.isDescending !== undefined) params.set('isDescending', String(input.isDescending))
  if (input.status !== undefined) params.set('status', String(input.status))
  if (input.roleId !== undefined) params.set('roleId', String(input.roleId))
  if (input.accountType !== undefined) params.set('accountType', String(input.accountType))
  if (input.departmentId) params.set('departmentId', input.departmentId)
  return params.toString()
}

const getList = (input: UserGetListInput): Promise<ApiPaginationResponse<UserListItemVm[]>> =>
  http.get(`${apiUser}?${toQuery(input)}`)
const getDetail = (id: string): Promise<ApiResponse<UserDetailVm>> => http.get(apiUserById(id))
const toFormData = (data: Record<string, any>): FormData => {
  const formData = new FormData()
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || value === null) continue
    if (value instanceof File) {
      formData.append(key, value)
    } else if (Array.isArray(value) && value.some((item) => item && typeof item === 'object')) {
      // Mảng object (vd DepartmentPositions) — [FromForm] cần encode theo index-key dạng
      // `DepartmentPositions[0].DepartmentID`, không gửi JSON string cho multipart.
      for (const [index, item] of value.entries()) {
        for (const [itemKey, itemValue] of Object.entries(item ?? {})) {
          if (itemValue !== undefined && itemValue !== null) {
            formData.append(`${key}[${index}].${itemKey}`, String(itemValue))
          }
        }
      }
    } else if (Array.isArray(value)) {
      // Mảng giá trị đơn (vd RoleIDs) — append từng phần tử để [FromForm] bind thành list.
      for (const item of value) {
        if (item !== undefined && item !== null) formData.append(key, String(item))
      }
    } else {
      formData.append(key, String(value))
    }
  }
  return formData
}

const create = (body: UserCreateInput): Promise<ApiResponse> =>
  http.post(apiUser, toFormData(body), {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

const update = (id: string, body: UserUpdateInput): Promise<ApiResponse> =>
  http.put(apiUserById(id), body, {
    headers: { 'Content-Type': 'application/json' },
  })
const updateAvatar = (id: string, avatarFile: File): Promise<ApiResponse> => {
  const formData = new FormData()
  formData.append('avatarFile', avatarFile)

  return http.put(apiUserAvatar(id), formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}
const deleteById = (id: string): Promise<ApiResponse> => http.delete(apiUserById(id))
const batchDelete = (ids: string[]): Promise<ApiResponse> => http.post(apiUserBulkDelete, { ids })
const bulkLock = (
  ids: string[]
): Promise<ApiResponse<{ successCount: number; skippedCount: number; message: string }>> =>
  http.post(apiUserBulkLock, { ids })
const bulkUnlock = (
  ids: string[]
): Promise<ApiResponse<{ successCount: number; skippedCount: number; message: string }>> =>
  http.post(apiUserBulkUnlock, { ids })
const bulkLogout = (
  ids: string[]
): Promise<ApiResponse<{ loggedOutCount: number; skippedIds: string[] }>> =>
  http.post(apiUserBulkLogout, { ids })
const logout = (id: string): Promise<ApiResponse> => http.post(apiUserLogout(id))
const lock = (id: string): Promise<ApiResponse> => http.post(apiUserLock(id))
const unlock = (id: string): Promise<ApiResponse> => http.post(apiUserUnlock(id))
const resetPassword = (id: string): Promise<ApiResponse> => http.post(apiUserResetPassword(id))
const getCombobox = (): Promise<
  ApiResponse<{ id: string; fullName: string; userName: string }[]>
> => http.get(apiUserCombobox)
const getDepartmentPositionCombobox = (): Promise<
  ApiResponse<UserDepartmentPositionComboboxVm[]>
> => http.get(apiUserDepartmentPositionCombobox)
const getProfile = (): Promise<ApiResponse<UserDetailVm>> => http.get(apiUserProfile)
const updateProfile = (body: {
  fullName: string
  email: string
  phoneNumber?: string
  address?: string
}): Promise<ApiResponse> => {
  return http.put(apiUserProfile, body, {
    headers: { 'Content-Type': 'application/json' },
  })
}
const updateProfileAvatar = (avatarFile: File): Promise<ApiResponse> => {
  const formData = new FormData()
  formData.append('avatarFile', avatarFile)

  return http.put(apiUserProfileAvatar, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

const userService = {
  getList,
  getDetail,
  create,
  update,
  updateAvatar,
  deleteById,
  batchDelete,
  bulkLock,
  bulkUnlock,
  bulkLogout,
  logout,
  lock,
  unlock,
  resetPassword,

  getCombobox,
  getDepartmentPositionCombobox,
  getProfile,
  updateProfile,
  updateProfileAvatar,
}
export default userService
